// Device metadata interface matching the iOS DeviceMetadata structure
export interface DeviceMetadata {
    osVersion: string;
    deviceModel: string;
    appVersion: string;
    batteryLevel: number;
    isCharging: boolean;
    timestamp: string;
    timeZone: string;
    locale: string;
    appBuildNumber: string;
    deviceIdentifier: string;
    networkConnectionType: string;
    pushNotificationToken?: string;
    device_type: string;
    deviceBatteryLevel?: number; // BLE device battery level in volts
    deviceFirmwareVersion?: string; // BLE device firmware version
}

// Function to collect device metadata (similar to iOS DeviceMetadata.collect)
export const collectDeviceMetadata = (
    deviceBatteryLevel?: number,
    deviceFirmwareVersion?: string,
    includeTimestamp: boolean = true
): DeviceMetadata => {
    // Get basic device info
    const userAgent = navigator.userAgent;
    
    // Extract OS version from user agent
    const getOSVersion = () => {
        if (userAgent.includes('Windows NT')) {
            const match = userAgent.match(/Windows NT (\d+\.\d+)/);
            return match ? `Windows ${match[1]}` : 'Windows';
        } else if (userAgent.includes('Mac OS X')) {
            const match = userAgent.match(/Mac OS X (\d+_\d+_\d+)/);
            return match ? `macOS ${match[1].replace(/_/g, '.')}` : 'macOS';
        } else if (userAgent.includes('Linux')) {
            return 'Linux';
        }
        return 'Unknown OS';
    };

    // Get device model/browser info
    const getDeviceModel = () => {
        if (userAgent.includes('Chrome')) {
            const match = userAgent.match(/Chrome\/(\d+\.\d+)/);
            return match ? `Chrome ${match[1]}` : 'Chrome';
        } else if (userAgent.includes('Firefox')) {
            const match = userAgent.match(/Firefox\/(\d+\.\d+)/);
            return match ? `Firefox ${match[1]}` : 'Firefox';
        } else if (userAgent.includes('Safari')) {
            const match = userAgent.match(/Version\/(\d+\.\d+)/);
            return match ? `Safari ${match[1]}` : 'Safari';
        }
        return 'Unknown Browser';
    };

    // Get network connection type
    const getNetworkConnectionType = () => {
        if ('connection' in navigator) {
            const connection = (navigator as any).connection;
            return connection?.effectiveType || 'unknown';
        }
        return 'unknown';
    };

    // Get app version from package.json or environment
    const getAppVersion = () => {
        try {
            return (process?.env?.REACT_APP_VERSION) || '1.0.0';
        } catch {
            return '1.0.0';
        }
    };

    // Get build number
    const getBuildNumber = () => {
        try {
            return (process?.env?.REACT_APP_BUILD_NUMBER) || '1';
        } catch {
            return '1';
        }
    };

    // Get device identifier (create a stable identifier)
    const getDeviceIdentifier = () => {
        let identifier = localStorage.getItem('deviceIdentifier');
        if (!identifier) {
            identifier = 'web-' + Math.random().toString(36).substr(2, 9);
            localStorage.setItem('deviceIdentifier', identifier);
        }
        return identifier;
    };

    // Get battery level (web doesn't have direct access, so we use a placeholder)
    const getBatteryLevel = () => {
        // Web doesn't have reliable battery API access, return placeholder
        return 1.0; // Assume fully charged
    };

    const result = {
        osVersion: getOSVersion(),
        deviceModel: getDeviceModel(),
        appVersion: getAppVersion(),
        batteryLevel: getBatteryLevel(),
        isCharging: false, // Web can't reliably detect charging state
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        locale: navigator.language,
        appBuildNumber: getBuildNumber(),
        deviceIdentifier: getDeviceIdentifier(),
        networkConnectionType: getNetworkConnectionType(),
        pushNotificationToken: undefined, // Web push tokens would be handled differently
        device_type: 'web',
        deviceBatteryLevel: deviceBatteryLevel,
        deviceFirmwareVersion: deviceFirmwareVersion
    } as DeviceMetadata;

    if (includeTimestamp) {
        result.timestamp = new Date().toISOString();
    }

    return result;
};